/**
 * V2 Auth Layout – same structure as V1, viewport-fixed height with scroll.
 * Responsive: sidebar becomes overlay drawer on small screens with hamburger toggle.
 */
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Wrapper, ContentWrapper } from "./AuthLayoutV2.style";
import HeaderV2 from "../shell/HeaderV2";
import NavigationV2 from "../shell/NavigationV2";
import { Text } from "../../components/styledConstants";
import { getPageTitle } from "../core/pageTitles";
import { PageTitleProvider, useCustomPageTitle } from "../core/PageTitleContext";
import { PageContainer } from "../design-system";
import RequestV2 from "../../api/RequestV2";
import Config from "../../utils/config";
import { getProfile } from "../../store/actions/user";
import { isEmpty } from "../../utils/common";

function AuthLayoutV2Inner({ children, unauthorized = false }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isWelcome = pathname === "/welcome";
  const customTitle = useCustomPageTitle();
  const pageTitle = customTitle ?? getPageTitle(pathname);
  const showBackArrow = !isWelcome && pathname !== "/dashboard";

  useEffect(() => {
    if (isEmpty(user)) {
      const api = new RequestV2(
        "",
        (data) => dispatch(getProfile(data?.data ?? data)),
        () => {}
      );
      api.get(Config.apis.user.GET_PROFILE);
    }
  }, [dispatch, user]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <Wrapper className="flex">
      <div
        className="sidebar-overlay"
        role="button"
        tabIndex={sidebarOpen ? 0 : -1}
        aria-hidden={!sidebarOpen}
        aria-label="Close menu"
        onClick={() => setSidebarOpen(false)}
        onKeyDown={(e) => {
          if (sidebarOpen && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setSidebarOpen(false);
          }
        }}
      />
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <NavigationV2 />
      </aside>
      <main className={`main-content ${isWelcome ? "welcome-page" : ""}`}>
        <div className="v2-header-bar">
          <button
            type="button"
            className="v2-header-menu"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={sidebarOpen}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {showBackArrow && (
            <button
              type="button"
              className="v2-header-back"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {!isWelcome && <h1 className="v2-page-title">{pageTitle}</h1>}
          <HeaderV2 />
        </div>
        <ContentWrapper className={isWelcome ? "welcome-page" : undefined}>
          {unauthorized ? (
            <PageContainer>
              <div
                style={{
                  padding: "2rem",
                  textAlign: "center",
                  color: "#721c24",
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c6cb",
                  borderRadius: "8px",
                  margin: "2rem auto",
                  maxWidth: "500px",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                🚫 You are not authorized to access this page!
              </div>
            </PageContainer>
          ) : (
            <PageContainer className={isWelcome ? "page-container-welcome" : undefined}>
              {children}
            </PageContainer>
          )}
        </ContentWrapper>
        <Text as="footer" className="copyrights" size="xsm" fw="light" color="color15">
          Copyrights © 2023. Mufin Pay. All rights reserved.{" "}
          <a
            style={{ color: "#333" }}
            target="_blank"
            rel="noreferrer"
            href="https://mufinpay.com/terms-and-conditions/"
          >
            Terms of use
          </a>{" "}
          |{" "}
          <a style={{ color: "#333" }} target="_blank" rel="noreferrer" href="https://mufinpay.com/privacy-policy/">
            Privacy policy
          </a>
        </Text>
      </main>
    </Wrapper>
  );
}

export default function AuthLayoutV2(props) {
  return (
    <PageTitleProvider>
      <AuthLayoutV2Inner {...props} />
    </PageTitleProvider>
  );
}
